import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Hook for screen CRUD operations, reordering, and name editing
 */
const useScreenOperations = ({
  screens,
  setScreens,
  currentScreenIndex,
  setCurrentScreenIndex,
  deviceMode,
  deviceDimensions,
  setConfirmDialog,
  setShowBulkAddModal,
  setActivePanel,
}) => {
  // Screen drag state
  const [draggingScreenIndex, setDraggingScreenIndex] = useState(null);
  const [dragOverScreenIndex, setDragOverScreenIndex] = useState(null);

  // Screen name editing state
  const [editingScreenId, setEditingScreenId] = useState(null);
  const [editingScreenName, setEditingScreenName] = useState('');

  // Find the next available "Screen N" name that doesn't collide with existing screens
  const nextAvailableScreenName = useCallback((existingScreens, startN) => {
    const taken = new Set(existingScreens.map(s => s.name?.toLowerCase()));
    let n = startN;
    while (taken.has(`screen ${n}`.toLowerCase())) {
      n += 1;
    }
    return { name: `Screen ${n}`, next: n + 1 };
  }, []);

  // Create a new screen with default layout
  const createNewScreen = useCallback((name, index) => {
    const dims = deviceDimensions[deviceMode];
    return {
      id: `screen_${Date.now()}_${index}`,
      name: name || `Screen ${index + 1}`,
      rank: index,
      elements: [],
      layout: {
        background: '#ffffff',
        padding: 20,
        width: dims.width,
        height: dims.height,
        deviceMode: deviceMode
      }
    };
  }, [deviceMode, deviceDimensions]);

  // Add a single new screen
  const addNewScreen = useCallback(() => {
    const { name } = nextAvailableScreenName(screens, screens.length + 1);
    const newScreen = createNewScreen(name, screens.length);
    setScreens([...screens, newScreen]);
    setCurrentScreenIndex(screens.length);
    setActivePanel?.('elements');
  }, [screens, setScreens, setCurrentScreenIndex, createNewScreen, setActivePanel, nextAvailableScreenName]);

  // Add multiple screens at once
  const addBulkScreens = useCallback((count) => {
    const newScreens = [];
    const startIndex = screens.length;
    let cursor = startIndex + 1;

    for (let i = 0; i < count; i++) {
      const { name, next } = nextAvailableScreenName(
        [...screens, ...newScreens],
        cursor
      );
      cursor = next;
      newScreens.push(createNewScreen(name, startIndex + i));
    }

    setScreens([...screens, ...newScreens]);
    setCurrentScreenIndex(startIndex);
    setShowBulkAddModal(false);
    setActivePanel?.('elements');
  }, [screens, setScreens, setCurrentScreenIndex, setShowBulkAddModal, createNewScreen, setActivePanel, nextAvailableScreenName]);

  // Delete a screen with confirmation
  const deleteScreen = useCallback((index) => {
    if (screens.length <= 1) {
      toast.warning('Cannot delete the last screen');
      return;
    }
    
    const screenToDelete = screens[index];
    const elementCount = screenToDelete.elements?.length || 0;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Screen?',
      message: `Are you sure you want to delete "${screenToDelete.name}"?${elementCount > 0 ? `\n\nThis screen contains ${elementCount} element${elementCount > 1 ? 's' : ''} that will be permanently removed.` : ''}`,
      type: 'danger',
      onConfirm: () => {
        const newScreens = screens.filter((_, i) => i !== index);
        setScreens(newScreens);
        if (currentScreenIndex >= newScreens.length) {
          setCurrentScreenIndex(newScreens.length - 1);
        }
        setConfirmDialog({ isOpen: false });
        toast.success(`Screen "${screenToDelete.name}" deleted`);
      }
    });
  }, [screens, setScreens, currentScreenIndex, setCurrentScreenIndex, setConfirmDialog]);

  // Delete all screens and reset to one empty screen
  const deleteAllScreens = useCallback(() => {
    if (screens.length <= 1 && (!screens[0]?.elements || screens[0].elements.length === 0)) {
      toast.info('No screens to delete');
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Screens?',
      message: `This will delete all ${screens.length} screen${screens.length !== 1 ? 's' : ''} and their elements. Items will remain in the sidebar. This cannot be undone.`,
      type: 'danger',
      onConfirm: () => {
        setScreens([createNewScreen('Screen 1', 0)]);
        setCurrentScreenIndex(0);
        setConfirmDialog({ isOpen: false });
        toast.success(`Deleted ${screens.length} screens`);
      }
    });
  }, [screens, setScreens, setCurrentScreenIndex, setConfirmDialog, createNewScreen]);

  // Duplicate a screen
  const duplicateScreen = useCallback((index) => {
    const screenToCopy = screens[index];
    
    // Find existing copies to determine the next copy number
    const baseName = screenToCopy.name.replace(/\s*\(Copy\s*\d*\)?\s*$/, '');
    const copyPattern = new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(Copy\\s*(\\d*)\\)?$`);
    
    const existingCopyNumbers = screens
      .map(s => {
        const match = s.name.match(copyPattern);
        if (match) {
          return match[1] ? parseInt(match[1]) : 1;
        }
        return null;
      })
      .filter(n => n !== null);
    
    const nextCopyNumber = existingCopyNumbers.length > 0 
      ? Math.max(...existingCopyNumbers) + 1 
      : 1;
    
    const newScreen = {
      ...screenToCopy,
      id: `screen_${Date.now()}`,
      name: `${baseName} (Copy ${nextCopyNumber})`,
      rank: index + 1
    };
    
    // Insert the new screen right after the current one
    const updatedScreens = [
      ...screens.slice(0, index + 1),
      newScreen,
      ...screens.slice(index + 1)
    ];
    
    // Update ranks for all screens after insertion
    const rerankedScreens = updatedScreens.map((screen, idx) => ({
      ...screen,
      rank: idx
    }));
    
    setScreens(rerankedScreens);
    setCurrentScreenIndex(index + 1);
  }, [screens, setScreens, setCurrentScreenIndex]);

  // Screen drag-and-drop handlers
  const handleScreenDragStart = useCallback((e, index) => {
    setDraggingScreenIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  }, []);

  const handleScreenDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggingScreenIndex !== null && draggingScreenIndex !== index) {
      setDragOverScreenIndex(index);
    }
  }, [draggingScreenIndex]);

  const handleScreenDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    
    if (draggingScreenIndex === null || draggingScreenIndex === dropIndex) {
      return;
    }
    
    // Don't do anything if dropping in the position right after the dragged screen
    if (dropIndex === draggingScreenIndex + 1) {
      setDraggingScreenIndex(null);
      setDragOverScreenIndex(null);
      return;
    }

    const newScreens = [...screens];
    const draggedScreen = newScreens[draggingScreenIndex];
    
    // Remove from old position
    newScreens.splice(draggingScreenIndex, 1);
    
    // Insert at new position
    newScreens.splice(dropIndex, 0, draggedScreen);
    
    // Update ranks
    newScreens.forEach((screen, idx) => {
      screen.rank = idx;
    });
    
    setScreens(newScreens);
    
    // Update current screen index if needed
    if (currentScreenIndex === draggingScreenIndex) {
      setCurrentScreenIndex(dropIndex);
    } else if (draggingScreenIndex < currentScreenIndex && dropIndex >= currentScreenIndex) {
      setCurrentScreenIndex(currentScreenIndex - 1);
    } else if (draggingScreenIndex > currentScreenIndex && dropIndex <= currentScreenIndex) {
      setCurrentScreenIndex(currentScreenIndex + 1);
    }
    
    setDraggingScreenIndex(null);
    setDragOverScreenIndex(null);
  }, [screens, setScreens, draggingScreenIndex, currentScreenIndex, setCurrentScreenIndex]);

  const handleScreenDragEnd = useCallback(() => {
    setDraggingScreenIndex(null);
    setDragOverScreenIndex(null);
    
    // Safety check: ensure currentScreenIndex is valid
    // This handles cases where drag ends outside the tab area
    if (currentScreenIndex < 0 || currentScreenIndex >= screens.length) {
      setCurrentScreenIndex(Math.max(0, Math.min(currentScreenIndex, screens.length - 1)));
    }
  }, [currentScreenIndex, screens.length, setCurrentScreenIndex]);

  // Screen name editing
  const startEditingScreenName = useCallback((screenId, currentName) => {
    setEditingScreenId(screenId);
    setEditingScreenName(currentName);
  }, []);

  const saveScreenName = useCallback((screenId) => {
    const trimmed = editingScreenName.trim();
    if (!trimmed) {
      setEditingScreenId(null);
      setEditingScreenName('');
      return;
    }

    const isDuplicate = screens.some(
      s => s.id !== screenId && s.name?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      // FECOA-1465: reject and revert to the original name so it is clear the rename did not
      // save. Leaving the editor open with the duplicate text reads as if it was accepted.
      toast.error(`A screen named "${trimmed}" already exists. Screen names must be unique.`);
      setEditingScreenId(null);
      setEditingScreenName('');
      return;
    }

    setScreens(screens.map(screen =>
      screen.id === screenId
        ? { ...screen, name: trimmed }
        : screen
    ));
    setEditingScreenId(null);
    setEditingScreenName('');
  }, [screens, setScreens, editingScreenName]);

  const cancelEditingScreenName = useCallback(() => {
    setEditingScreenId(null);
    setEditingScreenName('');
  }, []);

  return {
    // State
    draggingScreenIndex,
    setDraggingScreenIndex,
    dragOverScreenIndex,
    setDragOverScreenIndex,
    editingScreenId,
    setEditingScreenId,
    editingScreenName,
    setEditingScreenName,
    
    // Screen CRUD
    createNewScreen,
    addNewScreen,
    addBulkScreens,
    deleteScreen,
    deleteAllScreens,
    duplicateScreen,
    
    // Screen drag handlers
    handleScreenDragStart,
    handleScreenDragOver,
    handleScreenDrop,
    handleScreenDragEnd,
    
    // Screen name editing
    startEditingScreenName,
    saveScreenName,
    cancelEditingScreenName,
  };
};

export default useScreenOperations;
